import { Link, NavLink, Outlet } from 'react-router-dom'
import { BookOpen, Home, Library, LogOut, Megaphone, Shield, Trophy, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useMe } from '@/api/hooks'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import { Logo } from '@/components/Logo'
import { SectionSwitcher } from '@/components/SectionSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'

type NavItem = {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
}

/**
 * App chrome (pass-2 design): a fixed left **ink** sidebar on desktop
 * (logo, section switcher, primary nav, theme toggle + sign-out) and a compact
 * top bar + bottom tab bar on mobile. The sidebar/login chrome stays dark in
 * both themes — it uses the shared `--ink-*` tokens, not the theme surfaces.
 *
 * Visibility gating is preserved exactly: Knowledgebase = feature flag
 * (`me.features.knowledgeBase`), My Team = customer-side admin with an org
 * (`canViewTeamAnalytics && !isInternal && organization`), Admin CMS =
 * `canManageContent`.
 */
export function AppShell() {
  const { data: me } = useMe()

  const signOut = async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // Even if the call fails, send the user to login — the session is the
      // server's source of truth and they intend to leave.
    }
    window.location.assign('/login')
  }

  const orgPath = me?.organization ? `/orgs/${encodeURIComponent(me.organization.id)}` : null
  const showMyTeam = !!(me?.canViewTeamAnalytics && !me.isInternal && orgPath)

  // Primary nav (everything above the Admin divider).
  const primary: NavItem[] = [
    { to: '/journey', label: 'Your Journey', icon: Home },
    { to: '/', label: 'Library', icon: Library, end: true },
    { to: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    ...(me?.features.knowledgeBase
      ? [{ to: '/knowledge-base', label: 'Knowledgebase', icon: BookOpen } as NavItem]
      : []),
    // Flag-gated "What's New" — a global page (not a section), visible to everyone
    // when the whats_new flag is on for this user (clients + staff alike).
    ...(me?.features.whatsNew
      ? [{ to: '/whats-new', label: "What's New", icon: Megaphone } as NavItem]
      : []),
    ...(showMyTeam ? [{ to: orgPath!, label: 'My Team', icon: Users } as NavItem] : []),
  ]

  // Mobile bottom bar is capped at 4: the three learner staples plus the most
  // relevant secondary destination for this user.
  const fourthTab: NavItem | null = me?.canManageContent
    ? { to: '/admin', label: 'Admin', icon: Shield }
    : showMyTeam
      ? { to: orgPath!, label: 'Team', icon: Users }
      : me?.features.knowledgeBase
        ? { to: '/knowledge-base', label: 'Help', icon: BookOpen }
        : null
  const mobileTabs: NavItem[] = [
    { to: '/journey', label: 'Journey', icon: Home },
    { to: '/', label: 'Library', icon: Library, end: true },
    { to: '/leaderboard', label: 'Board', icon: Trophy },
    ...(fourthTab ? [fourthTab] : []),
  ]

  return (
    <div className="flex min-h-full">
      {/* Skip link (WCAG 2.4.1) — first focusable element; jumps past the nav. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-surface focus:px-4 focus:py-2 focus:text-body-sm focus:font-semibold focus:text-text-primary focus:shadow-[var(--shadow-md)]"
      >
        Skip to main content
      </a>

      {/* ---- Desktop sidebar (always ink, both themes) ---- */}
      <aside
        className="sticky top-0 hidden h-screen shrink-0 flex-col bg-ink text-ink-text md:flex"
        style={{ width: 'var(--layout-sidebar-width)' }}
      >
        <div className="flex h-full flex-col gap-6 px-4 py-5">
          <Link to="/" className="rounded-md px-2" aria-label="ShopView Academy home">
            <Logo variant="dark" />
          </Link>

          {/* Section switcher under a mono eyebrow (only when >1 section). */}
          <div className="flex flex-col gap-2">
            <p className="text-mono px-2 text-[11px] uppercase tracking-[0.16em] text-ink-text-3">
              Library
            </p>
            <SectionSwitcher onInk />
          </div>

          <nav className="flex flex-1 flex-col gap-1" aria-label="Primary">
            {primary.map((item) => (
              <SidebarLink key={item.to} item={item} />
            ))}

            {me?.canManageContent && (
              <>
                <div className="my-3 h-px bg-ink-border" />
                <SidebarLink item={{ to: '/admin', label: 'Admin CMS', icon: Shield }} />
              </>
            )}
          </nav>

          <div className="flex items-center justify-between gap-2 border-t border-ink-border pt-4">
            <ThemeToggle onInk />
            <button
              type="button"
              onClick={() => void signOut()}
              title="Sign out"
              aria-label="Sign out"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink-border text-ink-text-2 transition-colors hover:text-ink-text"
            >
              <LogOut className="h-4 w-4" aria-hidden strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {/* ---- Content column ---- */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-surface/90 px-4 py-2.5 backdrop-blur md:hidden">
          <Link to="/" className="shrink-0 rounded-md" aria-label="ShopView Academy home">
            <Logo />
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => void signOut()}
              title="Sign out"
              aria-label="Sign out"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-text-secondary transition-colors hover:text-text-primary"
            >
              <LogOut className="h-4 w-4" aria-hidden strokeWidth={1.75} />
            </button>
          </div>
        </header>
        {/* Mobile section switcher row */}
        <div className="border-b border-border px-4 py-2 md:hidden">
          <SectionSwitcher />
        </div>

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-[var(--layout-max-width)] flex-1 px-4 py-8 pb-24 outline-none sm:px-6 sm:py-10 md:pb-12"
        >
          <Outlet />
        </main>

        {/* Mobile bottom tab bar */}
        <nav
          className="fixed inset-x-0 bottom-0 z-20 flex border-t border-border bg-surface/95 backdrop-blur md:hidden"
          aria-label="Bottom navigation"
        >
          {mobileTabs.map((tab) => (
            <MobileTab key={tab.label} item={tab} />
          ))}
        </nav>
      </div>
    </div>
  )
}

/** Desktop sidebar link — active = brand-filled pill, idle = muted ink text. */
function SidebarLink({ item: { to, label, icon: Icon, end } }: { item: NavItem }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'text-body flex items-center gap-3 rounded-lg px-3 py-2.5 font-medium transition-colors duration-150 ease-out',
          isActive
            ? 'bg-brand text-white shadow-[var(--shadow-sm)]'
            : 'text-ink-text-2 hover:bg-ink-3 hover:text-ink-text',
        )
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden strokeWidth={1.75} />
      <span className="truncate">{label}</span>
    </NavLink>
  )
}

/** Mobile bottom-bar tab — icon over label, active in brand. */
function MobileTab({ item: { to, label, icon: Icon, end } }: { item: NavItem }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold transition-colors',
          isActive ? 'text-brand' : 'text-text-tertiary',
        )
      }
    >
      <Icon className="h-5 w-5" aria-hidden strokeWidth={1.75} />
      {label}
    </NavLink>
  )
}
